<template>
  <div class="report-page-wrapper">
    <vue-element-loading
      :active="appLoading"
      :is-full-screen="true"
      background-color="#FFFFFF"
      color="#9a6b27"
      spinner="bar-fade-scale"
    />
    <ServerError v-if="ServerError" />

    <!-- Executive Toast Notification -->
    <v-snackbar v-model="showsnackbar" color="#1e293b" right :timeout="timeout">
      <div class="d-flex align-center justify-space-between text-white" style="font-size: 13px;">
        <span>{{ msg }}</span>
        <v-btn small icon text @click="showsnackbar = false" class="ml-2">
          <v-icon small color="white">mdi-close</v-icon>
        </v-btn>
      </div>
    </v-snackbar>

    <!-- Top Executive Header Card -->
    <v-card flat class="header-card pa-4 mb-4 rounded-lg">
      <div class="d-flex flex-wrap align-center justify-space-between" style="gap: 16px;">
        <!-- Left: Page Identity -->
        <div class="d-flex align-center">
          <div class="header-icon-box mr-3">
            <v-icon color="#0f172a" size="22">mdi-swap-horizontal-bold</v-icon>
          </div>
          <div>
            <div class="page-title">DAILY ISSUE &amp; RECEIPT REPORT</div>
            <div class="page-subtitle">Voucher-wise breakdown of metal issues, receipts &amp; supplier settlements</div>
          </div>
        </div>

        <!-- Right: Actions & Filters -->
        <div class="d-flex flex-wrap align-center" style="gap: 10px;">
          <!-- Quick Date Presets -->
          <div class="d-none d-sm-flex align-center date-preset-group mr-1">
            <button
              class="date-preset-btn"
              :class="{ 'date-preset-btn-active': isToday }"
              @click="setToday()"
            >
              Today
            </button>
            <button
              class="date-preset-btn"
              :class="{ 'date-preset-btn-active': isYesterday }"
              @click="setYesterday()"
            >
              Yesterday
            </button>
          </div>

          <!-- Date Picker -->
          <div class="date-input-wrap">
            <v-menu
              ref="menu"
              v-model="menu"
              :close-on-content-click="false"
              :return-value.sync="date"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="date"
                  outlined
                  background-color="white"
                  readonly
                  hide-details
                  dense
                  prepend-inner-icon="mdi-calendar-range"
                  class="pro-input date-input"
                  v-bind="attrs"
                  v-on="on"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="date"
                no-title
                scrollable
                color="#9a6b27"
                @change="$refs.menu.save(date)"
              >
                <v-spacer></v-spacer>
              </v-date-picker>
            </v-menu>
          </div>

          <!-- Refresh Button -->
          <v-btn
            icon
            small
            title="Refresh Report"
            class="refresh-icon-btn"
            @click="getList()"
          >
            <v-icon small color="#64748b">mdi-refresh</v-icon>
          </v-btn>

          <!-- Download PDF Button -->
          <v-btn
            dark
            color="#9a6b27"
            class="btn-primary text-capitalize elevation-1 font-weight-bold"
            @click="download()"
          >
            <v-icon left small>mdi-file-pdf-box</v-icon>
            Download PDF
          </v-btn>
        </div>
      </div>
    </v-card>

    <!-- Executive KPI Metric Cards (4 Grid) -->
    <div class="kpi-grid mb-4">
      <!-- 1. Total Vouchers -->
      <div class="kpi-card">
        <div class="kpi-content">
          <div class="kpi-label">TOTAL VOUCHERS</div>
          <div class="kpi-value">{{ voucherRows.length }} <span class="kpi-unit">records</span></div>
          <div class="kpi-hint">{{ issueCount }} Issues · {{ receiptCount }} Receipts</div>
        </div>
        <div class="kpi-icon-pill" style="background: #eff6ff; color: #2563eb;">
          <v-icon size="20" color="#2563eb">mdi-file-document-multiple-outline</v-icon>
        </div>
      </div>

      <!-- 2. Issues Summary -->
      <div class="kpi-card" @click="typeFilter = (typeFilter === 'Issue' ? 'ALL' : 'Issue')" :class="{ 'kpi-card-active': typeFilter === 'Issue' }">
        <div class="kpi-content">
          <div class="kpi-label">TOTAL ISSUES</div>
          <div class="kpi-value">{{ formatWeight(issueWeight) }} <span class="kpi-unit">gm</span></div>
          <div class="kpi-hint">{{ issueCount }} vouchers · ₹{{ formatCurrency(issueAmount) }}</div>
        </div>
        <div class="kpi-icon-pill" style="background: #fff7ed; color: #ea580c;">
          <v-icon size="20" color="#ea580c">mdi-arrow-top-right</v-icon>
        </div>
      </div>

      <!-- 3. Receipts Summary -->
      <div class="kpi-card" @click="typeFilter = (typeFilter === 'Receipt' ? 'ALL' : 'Receipt')" :class="{ 'kpi-card-active': typeFilter === 'Receipt' }">
        <div class="kpi-content">
          <div class="kpi-label">TOTAL RECEIPTS</div>
          <div class="kpi-value">{{ formatWeight(receiptWeight) }} <span class="kpi-unit">gm</span></div>
          <div class="kpi-hint">{{ receiptCount }} vouchers · ₹{{ formatCurrency(receiptAmount) }}</div>
        </div>
        <div class="kpi-icon-pill" style="background: #f0fdf4; color: #16a34a;">
          <v-icon size="20" color="#16a34a">mdi-arrow-bottom-left</v-icon>
        </div>
      </div>

      <!-- 4. Total Amount -->
      <div class="kpi-card">
        <div class="kpi-content">
          <div class="kpi-label">TOTAL AMOUNT</div>
          <div class="kpi-value">₹{{ formatCurrency(totals.totalAmount) }}</div>
          <div class="kpi-hint">Total Net Weight: {{ formatWeight(totals.totalWeight) }} gm</div>
        </div>
        <div class="kpi-icon-pill" style="background: #fefce8; color: #ca8a04;">
          <v-icon size="20" color="#ca8a04">mdi-currency-inr</v-icon>
        </div>
      </div>
    </div>

    <!-- Filter & Search Bar -->
    <div class="filter-tab-bar mb-3 d-flex align-center justify-space-between flex-wrap" style="gap: 8px;">
      <!-- Type Filter Pills -->
      <div class="d-flex align-center flex-wrap" style="gap: 6px;">
        <button
          class="filter-pill"
          :class="{ 'filter-pill-active': typeFilter === 'ALL' }"
          @click="typeFilter = 'ALL'"
        >
          All Vouchers
          <span class="filter-pill-badge">{{ voucherRows.length }}</span>
        </button>
        <button
          class="filter-pill"
          :class="{ 'filter-pill-active': typeFilter === 'Issue' }"
          @click="typeFilter = 'Issue'"
        >
          Issues
          <span class="filter-pill-badge warning-pill-badge">{{ issueCount }}</span>
        </button>
        <button
          class="filter-pill"
          :class="{ 'filter-pill-active': typeFilter === 'Receipt' }"
          @click="typeFilter = 'Receipt'"
        >
          Receipts
          <span class="filter-pill-badge active-pill-badge">{{ receiptCount }}</span>
        </button>
      </div>

      <!-- Search and Settlement Mode Filter -->
      <div class="d-flex align-center flex-wrap" style="gap: 8px;">
        <!-- Quick Search -->
        <div class="search-input-wrap">
          <v-text-field
            v-model="searchQuery"
            clearable
            placeholder="Search voucher, supplier..."
            outlined
            dense
            hide-details
            prepend-inner-icon="mdi-magnify"
            class="search-input"
            color="#9a6b27"
          ></v-text-field>
        </div>

        <!-- Settlement Mode Dropdown -->
        <div class="mode-select-wrap" v-if="uniqueSettlementModes.length > 1">
          <v-select
            v-model="modeFilter"
            :items="['ALL', ...uniqueSettlementModes]"
            placeholder="All Modes"
            outlined
            dense
            hide-details
            class="mode-select"
            color="#9a6b27"
          ></v-select>
        </div>

        <div class="table-total-count text-muted" style="font-size: 12.5px;">
          Showing <span class="font-weight-bold text-slate-800">{{ filteredVoucherRows.length }}</span> of {{ voucherRows.length }} vouchers
        </div>
      </div>
    </div>

    <!-- Master Data Table Card -->
    <v-card flat class="content-table-card rounded-lg overflow-hidden">
      <div v-if="filteredVoucherRows && filteredVoucherRows.length > 0">
        <div class="table-responsive-wrapper">
          <table class="pro-master-table">
            <thead>
              <tr>
                <th style="width: 60px;" class="text-center">#</th>
                <th style="width: 140px;" class="text-left">Voucher No</th>
                <th style="width: 120px;" class="text-center">Type</th>
                <th style="min-width: 200px;" class="text-left">Supplier</th>
                <th style="width: 160px;" class="text-center">Settlement Mode</th>
                <th style="width: 140px;" class="text-right">Weight (gm)</th>
                <th style="width: 160px;" class="text-right">Amount (₹)</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(row, i) in filteredVoucherRows"
                :key="i"
                class="master-table-row"
              >
                <!-- Index -->
                <td class="text-center text-muted font-weight-medium">
                  {{ i + 1 }}
                </td>

                <!-- Voucher No -->
                <td>
                  <span class="code-pill">
                    <v-icon x-small color="#64748b" class="mr-1">mdi-receipt-outline</v-icon>
                    {{ row.voucherNo || '—' }}
                  </span>
                </td>

                <!-- Type -->
                <td class="text-center">
                  <span :class="getTypeBadgeClass(row.type)">
                    <v-icon x-small :color="row.type === 'Issue' ? '#ea580c' : '#16a34a'" class="mr-1">
                      {{ row.type === 'Issue' ? 'mdi-arrow-top-right' : 'mdi-arrow-bottom-left' }}
                    </v-icon>
                    {{ row.type || '—' }}
                  </span>
                </td>

                <!-- Supplier -->
                <td>
                  <div class="d-flex align-center">
                    <div class="supplier-avatar mr-2">
                      {{ getInitials(row.supplier) }}
                    </div>
                    <div class="supplier-name-text">{{ row.supplier || '—' }}</div>
                  </div>
                </td>

                <!-- Settlement Mode -->
                <td class="text-center">
                  <span :class="getModeBadgeClass(row.settlementMode)">
                    {{ row.settlementMode || '—' }}
                  </span>
                </td>

                <!-- Weight -->
                <td class="text-right">
                  <div class="weight-cell">
                    <span class="font-weight-semibold text-slate-900">{{ formatWeight(row.weight) }}</span>
                    <span class="unit-text ml-1">gm</span>
                  </div>
                </td>

                <!-- Amount -->
                <td class="text-right">
                  <span class="amount-text">₹ {{ formatCurrency(row.amount) }}</span>
                </td>
              </tr>
            </tbody>
            <tfoot>
              <tr class="total-row">
                <td colspan="5" class="text-right font-weight-bold total-label">
                  TOTAL ({{ filteredVoucherRows.length }} {{ filteredVoucherRows.length === 1 ? 'voucher' : 'vouchers' }}):
                </td>
                <td class="text-right font-weight-bold total-val">
                  {{ formatWeight(displayedTotalWeight) }} <span class="unit-text">gm</span>
                </td>
                <td class="text-right font-weight-bold total-val text-slate-900">
                  ₹ {{ formatCurrency(displayedTotalAmount) }}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      <!-- Empty State -->
      <div v-else class="empty-state-wrap py-12 text-center">
        <div class="empty-icon-circle mb-3">
          <v-icon size="36" color="#94a3b8">mdi-swap-horizontal-bold</v-icon>
        </div>
        <div class="empty-title">No Vouchers Recorded</div>
        <div class="empty-subtitle">
          {{ (searchQuery || typeFilter !== 'ALL' || modeFilter !== 'ALL') ? 'No issue or receipt vouchers match your active filters. Try resetting search or filters.' : 'No issue or receipt vouchers were found for the selected date (' + date + ').' }}
        </div>
        <div class="mt-4 d-flex justify-center" style="gap: 10px;">
          <v-btn
            v-if="searchQuery || typeFilter !== 'ALL' || modeFilter !== 'ALL'"
            small
            outlined
            color="#0f172a"
            class="text-capitalize"
            @click="clearFilters()"
          >
            <v-icon small left>mdi-filter-remove-outline</v-icon>
            Reset Filters
          </v-btn>
          <v-btn
            small
            dark
            color="#9a6b27"
            class="text-capitalize"
            to="/IssueReceipt"
          >
            <v-icon small left>mdi-plus</v-icon>
            Create Issue / Receipt
          </v-btn>
        </div>
      </div>
    </v-card>
  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "DailyIssueReceiptReport",
  data() {
    return {
      appLoading: false,
      ServerError: false,
      showsnackbar: false,
      timeout: 2500,
      msg: null,
      menu: false,
      date: new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
        .toISOString()
        .substr(0, 10),
      voucherRows: [],
      totals: { totalWeight: 0, totalAmount: 0 },
      searchQuery: "",
      typeFilter: "ALL",
      modeFilter: "ALL",
    };
  },

  watch: {
    date() {
      this.getList();
    },
  },

  mounted() {
    this.getList();
  },

  computed: {
    isToday() {
      const today = new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
        .toISOString()
        .substr(0, 10);
      return this.date === today;
    },

    isYesterday() {
      const y = new Date(Date.now() - new Date().getTimezoneOffset() * 60000 - 86400000)
        .toISOString()
        .substr(0, 10);
      return this.date === y;
    },

    issueCount() {
      return (this.voucherRows || []).filter((v) => (v.type || "").toLowerCase() === "issue").length;
    },

    receiptCount() {
      return (this.voucherRows || []).filter((v) => (v.type || "").toLowerCase() === "receipt").length;
    },

    issueWeight() {
      return (this.voucherRows || [])
        .filter((v) => (v.type || "").toLowerCase() === "issue")
        .reduce((sum, v) => sum + (Number(v.weight) || 0), 0);
    },

    receiptWeight() {
      return (this.voucherRows || [])
        .filter((v) => (v.type || "").toLowerCase() === "receipt")
        .reduce((sum, v) => sum + (Number(v.weight) || 0), 0);
    },

    issueAmount() {
      return (this.voucherRows || [])
        .filter((v) => (v.type || "").toLowerCase() === "issue")
        .reduce((sum, v) => sum + (Number(v.amount) || 0), 0);
    },

    receiptAmount() {
      return (this.voucherRows || [])
        .filter((v) => (v.type || "").toLowerCase() === "receipt")
        .reduce((sum, v) => sum + (Number(v.amount) || 0), 0);
    },

    uniqueSettlementModes() {
      const set = new Set();
      (this.voucherRows || []).forEach((v) => {
        if (v.settlementMode && v.settlementMode !== "-") {
          set.add(v.settlementMode);
        }
      });
      return Array.from(set);
    },

    filteredVoucherRows() {
      if (!this.voucherRows) return [];
      let list = this.voucherRows;

      // Filter by Type
      if (this.typeFilter !== "ALL") {
        list = list.filter((item) => (item.type || "").toLowerCase() === this.typeFilter.toLowerCase());
      }

      // Filter by Settlement Mode
      if (this.modeFilter !== "ALL") {
        list = list.filter((item) => (item.settlementMode || "").toLowerCase() === this.modeFilter.toLowerCase());
      }

      // Search Query
      if (this.searchQuery && this.searchQuery.trim()) {
        const q = this.searchQuery.trim().toLowerCase();
        list = list.filter((item) => {
          const vNo = (item.voucherNo || "").toLowerCase();
          const supp = (item.supplier || "").toLowerCase();
          const mode = (item.settlementMode || "").toLowerCase();
          const type = (item.type || "").toLowerCase();
          return vNo.includes(q) || supp.includes(q) || mode.includes(q) || type.includes(q);
        });
      }

      return list;
    },

    displayedTotalWeight() {
      return (this.filteredVoucherRows || []).reduce((sum, v) => sum + (Number(v.weight) || 0), 0);
    },

    displayedTotalAmount() {
      return (this.filteredVoucherRows || []).reduce((sum, v) => sum + (Number(v.amount) || 0), 0);
    },
  },

  methods: {
    setToday() {
      this.date = new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
        .toISOString()
        .substr(0, 10);
    },

    setYesterday() {
      this.date = new Date(Date.now() - new Date().getTimezoneOffset() * 60000 - 86400000)
        .toISOString()
        .substr(0, 10);
    },

    clearFilters() {
      this.searchQuery = "";
      this.typeFilter = "ALL";
      this.modeFilter = "ALL";
    },

    formatNumber(val) {
      if (val === null || val === undefined || isNaN(val)) return "0";
      return Number(val).toLocaleString("en-IN");
    },

    formatWeight(val) {
      if (val === null || val === undefined || isNaN(val)) return "0.00";
      return Number(val).toFixed(2);
    },

    formatCurrency(val) {
      if (val === null || val === undefined || isNaN(val)) return "0.00";
      return Number(val).toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },

    getInitials(name) {
      if (!name || name === "-") return "S";
      const parts = name.trim().split(" ");
      if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase();
      }
      return name.slice(0, 2).toUpperCase();
    },

    getTypeBadgeClass(type) {
      const t = (type || "").toLowerCase();
      if (t === "issue") return "type-pill type-issue";
      if (t === "receipt") return "type-pill type-receipt";
      return "type-pill type-default";
    },

    getModeBadgeClass(mode) {
      const m = (mode || "").toLowerCase();
      if (m.includes("cash")) return "mode-chip mode-cash";
      if (m.includes("bank") || m.includes("online") || m.includes("transfer") || m.includes("cheque")) return "mode-chip mode-bank";
      if (m.includes("metal") || m.includes("gold") || m.includes("silver")) return "mode-chip mode-metal";
      return "mode-chip mode-default";
    },

    getList() {
      if (!this.date) return;
      this.appLoading = true;
      axios({
        method: "get",
        url: "/data/dailyissuereceiptreport",
        headers: { "x-auth-token": localStorage.getItem("token") },
        params: {
          date: this.date,
        },
      })
        .then((response) => {
          this.appLoading = false;
          if (response.data.status) {
            this.voucherRows = response.data.data.voucherRows || [];
            this.totals = response.data.data.totals || { totalWeight: 0, totalAmount: 0 };
          } else {
            this.msg = response.data.msg;
            this.showsnackbar = true;
          }
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.log(err);
        });
    },

    download() {
      if (!this.date) {
        this.msg = "Please choose a date";
        this.showsnackbar = true;
        return;
      }
      this.appLoading = true;
      axios({
        method: "get",
        url: "/download/dailyissuereceiptreport",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
        params: {
          date: this.date,
        },
        responseType: "blob",
      })
        .then((response) => {
          this.appLoading = false;
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", `DailyIssueReceiptReport_${this.date}.pdf`);
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.log(err);
        });
    },
  },
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=Inter:wght@400;500;600;700&display=swap");

.report-page-wrapper {
  padding: 4px 0 40px 0;
  width: 100%;
  margin: 0 auto;
  font-family: "Plus Jakarta Sans", "Inter", -apple-system, BlinkMacSystemFont, sans-serif;
  color: #0f172a;
}

/* 1. Header Card */
.header-card {
  background: #ffffff !important;
  border: 1px solid #e2e8f0 !important;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02) !important;
}

.header-icon-box {
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.page-title {
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
  letter-spacing: 0.5px;
}

.page-subtitle {
  font-size: 12px;
  color: #64748b;
  margin-top: 1px;
}

/* Date Presets */
.date-preset-group {
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 2px;
}

.date-preset-btn {
  background: transparent;
  border: none;
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  padding: 6px 12px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.date-preset-btn:hover {
  color: #0f172a;
}

.date-preset-btn-active {
  background: #ffffff;
  color: #9a6b27;
  font-weight: 700;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.date-input-wrap {
  width: 155px;
}

.pro-input :deep(.v-input__slot) {
  min-height: 38px !important;
  border-radius: 8px !important;
  font-size: 13px !important;
  background: #ffffff !important;
}

.pro-input :deep(fieldset) {
  border-color: #cbd5e1 !important;
}

.pro-input.v-input--is-focused :deep(fieldset) {
  border-color: #9a6b27 !important;
  border-width: 1.5px !important;
}

.refresh-icon-btn {
  background: #f8fafc !important;
  border: 1px solid #e2e8f0 !important;
  border-radius: 8px !important;
  width: 38px !important;
  height: 38px !important;
}

.btn-primary {
  background: linear-gradient(135deg, #b9893f 0%, #875c24 100%) !important;
  background-color: #9a6b27 !important;
  color: #ffffff !important;
  font-size: 12.5px !important;
  font-weight: 600 !important;
  height: 38px !important;
  border-radius: 8px !important;
  box-shadow: 0 4px 14px rgba(154, 106, 44, 0.28) !important;
  transition: all 0.2s ease !important;
}

.btn-primary:hover {
  background: linear-gradient(135deg, #c59449 0%, #956627 100%) !important;
  box-shadow: 0 6px 18px rgba(154, 106, 44, 0.38) !important;
  transform: translateY(-1.5px);
}

/* 2. KPI Grid */
.kpi-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
}

@media (max-width: 1100px) {
  .kpi-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 600px) {
  .kpi-grid {
    grid-template-columns: 1fr;
  }
}

.kpi-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 14px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);
  transition: all 0.2s ease;
  cursor: pointer;
}

.kpi-card:hover {
  border-color: #cbd5e1;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

.kpi-card-active {
  border-color: #9a6b27 !important;
  background: #fffdfa !important;
  box-shadow: 0 0 0 2px rgba(154, 106, 44, 0.15) !important;
}

.kpi-label {
  font-size: 11px;
  font-weight: 700;
  color: #64748b;
  letter-spacing: 0.5px;
  text-transform: uppercase;
}

.kpi-value {
  font-size: 19px;
  font-weight: 800;
  color: #0f172a;
  letter-spacing: -0.3px;
  margin: 3px 0;
  line-height: 1.2;
}

.kpi-unit {
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
}

.kpi-hint {
  font-size: 11px;
  color: #94a3b8;
}

.kpi-icon-pill {
  width: 38px;
  height: 38px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

/* 3. Filter Bar */
.filter-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 999px;
  font-size: 12.5px;
  font-weight: 600;
  color: #475569;
  cursor: pointer;
  transition: all 0.2s ease;
}

.filter-pill:hover {
  border-color: #cbd5e1;
  color: #0f172a;
}

.filter-pill-active {
  background: #0f172a !important;
  color: #ffffff !important;
  border-color: #0f172a !important;
}

.filter-pill-badge {
  font-size: 11px;
  font-weight: 700;
  padding: 1px 7px;
  border-radius: 999px;
  background: #f1f5f9;
  color: #475569;
}

.filter-pill-active .filter-pill-badge {
  background: rgba(255, 255, 255, 0.2);
  color: #ffffff;
}

.warning-pill-badge {
  background: #fff7ed;
  color: #c2410c;
}

.active-pill-badge {
  background: #f0fdf4;
  color: #15803d;
}

.search-input-wrap {
  width: 200px;
}

.search-input :deep(.v-input__slot) {
  border-radius: 8px !important;
  border: 1px solid #cbd5e1 !important;
  min-height: 36px !important;
  font-size: 12.5px !important;
  background: #ffffff !important;
}

.mode-select-wrap {
  width: 140px;
}

.mode-select :deep(.v-input__slot) {
  border-radius: 8px !important;
  border: 1px solid #cbd5e1 !important;
  min-height: 36px !important;
  font-size: 12px !important;
  background: #ffffff !important;
}

/* 4. Table Card */
.content-table-card {
  background: #ffffff !important;
  border: 1px solid #e2e8f0 !important;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02) !important;
}

.table-responsive-wrapper {
  overflow-x: auto;
}

.pro-master-table {
  width: 100%;
  border-collapse: collapse;
}

.pro-master-table th {
  background-color: #f8fafc !important;
  color: #475569 !important;
  font-size: 11px !important;
  font-weight: 700 !important;
  text-transform: uppercase !important;
  letter-spacing: 0.5px !important;
  padding: 12px 16px !important;
  border-bottom: 1px solid #e2e8f0 !important;
}

.pro-master-table td {
  font-size: 13px !important;
  color: #334155 !important;
  padding: 12px 16px !important;
  border-bottom: 1px solid #f1f5f9 !important;
}

.master-table-row:hover {
  background-color: #f8fafc !important;
}

.total-row td {
  background-color: #f8fafc !important;
  border-top: 2px solid #e2e8f0 !important;
  border-bottom: none !important;
  padding: 14px 16px !important;
}

.total-label {
  color: #475569;
  font-size: 12.5px;
  letter-spacing: 0.3px;
}

.total-val {
  font-size: 14px;
  color: #0f172a;
}

.code-pill {
  display: inline-flex;
  align-items: center;
  font-family: monospace;
  font-weight: 700;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  padding: 3px 8px;
  border-radius: 6px;
  font-size: 11.5px;
  color: #1e293b;
}

.type-pill {
  display: inline-flex;
  align-items: center;
  font-size: 11px;
  font-weight: 700;
  padding: 3px 10px;
  border-radius: 999px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.type-issue {
  background: #fff7ed;
  color: #ea580c;
  border: 1px solid #fed7aa;
}

.type-receipt {
  background: #f0fdf4;
  color: #16a34a;
  border: 1px solid #bbf7d0;
}

.type-default {
  background: #f1f5f9;
  color: #475569;
  border: 1px solid #e2e8f0;
}

.supplier-avatar {
  width: 30px;
  height: 30px;
  border-radius: 8px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  color: #475569;
  font-weight: 700;
  font-size: 11.5px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.supplier-name-text {
  font-weight: 600;
  color: #0f172a;
  font-size: 13px;
}

.mode-chip {
  display: inline-block;
  font-size: 11px;
  font-weight: 600;
  padding: 2px 10px;
  border-radius: 6px;
  text-transform: capitalize;
}

.mode-cash {
  background: #ecfdf5;
  color: #059669;
  border: 1px solid #a7f3d0;
}

.mode-bank {
  background: #eff6ff;
  color: #2563eb;
  border: 1px solid #bfdbfe;
}

.mode-metal {
  background: #fefce8;
  color: #ca8a04;
  border: 1px solid #fef08a;
}

.mode-default {
  background: #f8fafc;
  color: #475569;
  border: 1px solid #e2e8f0;
}

.amount-text {
  font-weight: 700;
  color: #0f172a;
  font-size: 13.5px;
}

.unit-text {
  font-size: 11px;
  font-weight: 600;
  color: #64748b;
}

/* 5. Empty State */
.empty-state-wrap {
  padding: 60px 20px;
}

.empty-icon-circle {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.empty-title {
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
}

.empty-subtitle {
  font-size: 13px;
  color: #64748b;
  margin-top: 4px;
  max-width: 440px;
  margin-left: auto;
  margin-right: auto;
}
</style>
